import { useEffect, useState, useRef } from "react";
import { DateTime } from "luxon";

type MinuteChangeCallback = (currentTime: DateTime) => void;

interface UseCurrentTimeOptions {
    onMinuteChange?: MinuteChangeCallback | MinuteChangeCallback[];
    enableLogging?: boolean;
    timeShiftMs?: number;
}

export const useCurrentTime = (options: UseCurrentTimeOptions = {}) => {
    const [nowDate, setNowDate] = useState(DateTime.local());
    const [minuteTrigger, setMinuteTrigger] = useState(0);
    const { onMinuteChange, enableLogging = false } = options;

    const callbacksRef = useRef<MinuteChangeCallback[]>([]);

    useEffect(() => {
        if (onMinuteChange) {
            callbacksRef.current = Array.isArray(onMinuteChange) ? onMinuteChange : [onMinuteChange];
        } else {
            callbacksRef.current = [];
        }
    }, [onMinuteChange]);

    useEffect(() => {
        const now = DateTime.local();
        const msUntilNextMinute = (60 - now.second) * 1000 - now.millisecond;

        const timeout = setTimeout(() => {
            const newTime = DateTime.local();
            setNowDate(newTime);
            setMinuteTrigger((prev) => prev + 1);

            callbacksRef.current.forEach((callback) => {
                try {
                    callback(newTime);
                } catch (error) {
                    console.error("Error in minute change callback:", error);
                }
            });
        }, msUntilNextMinute + (options.timeShiftMs ?? 0));

        return () => clearTimeout(timeout);
    }, [nowDate, enableLogging]);

    return {
        nowDate,
        minuteTrigger,
        isNewMinute: minuteTrigger > 0,
        formattedTime: nowDate.toLocaleString(DateTime.TIME_WITH_SECONDS),
    };
};
